import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { Reveal } from "../components/Reveal";
import { Newspaper, ArrowRight } from "lucide-react";

function fmtDate(ts) {
  try {
    return new Date(ts * 1000).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

export default function NewsPage() {
  const { t } = useLang();
  const [patches, setPatches] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/patch-notes")
      .then(({ data }) => { if (alive) setPatches(data.items || []); })
      .catch(() => { if (alive) setPatches([]); });
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <main className="pt-32 pb-28" data-testid="news-page">
      <div className="max-w-4xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">// INTELLIGENCE REPORT</p>
          <h1 className="font-display text-6xl uppercase leading-[0.9] mb-4">
            WARDOGS <span className="text-wardogs">Patch Notes</span>
          </h1>
          <p className="text-steel max-w-xl mb-12">{t("pn.sub")}</p>
        </Reveal>

        {!patches && (
          <div className="flex flex-col gap-4" data-testid="news-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-6 animate-pulse">
                <div className="h-3 w-24 bg-[#1A1A1A] mb-3" />
                <div className="h-5 w-3/4 bg-[#1A1A1A] mb-2" />
                <div className="h-3 w-full bg-[#1A1A1A]" />
              </div>
            ))}
          </div>
        )}

        {patches && (
          <div className="flex flex-col gap-4" data-testid="patch-notes-section">
            {patches.map((p, i) => (
              <Reveal key={p.id} delay={Math.min(i * 0.04, 0.3)}>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] hover:border-wardogs/60 p-6 flex items-start gap-4 transition-colors duration-300 group block"
                  data-testid={`patch-note-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <span className="chamfer-sm px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] font-bold bg-wardogs/15 text-wardogs border border-wardogs/40">PATCH</span>
                      <span className="font-mono text-[10px] text-[#4A4D51]">{fmtDate(p.date)}</span>
                    </div>
                    <h2 className="font-display text-2xl uppercase leading-tight group-hover:text-wardogs transition-colors duration-300 mb-1.5">
                      {p.title}
                    </h2>
                    {p.excerpt && <p className="text-sm text-steel leading-relaxed line-clamp-3">{p.excerpt}</p>}
                  </div>
                  <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-wardogs shrink-0 mt-1">
                    {t("pn.open")} <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </a>
              </Reveal>
            ))}
            {patches.length === 0 && (
              <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-8 text-center" data-testid="patch-notes-empty">
                <Newspaper className="w-8 h-8 text-[#2A2A2A] mx-auto mb-3" />
                <p className="font-mono text-xs text-steel">La pattuglia controlla Steam ogni 15 minuti: il prossimo annuncio ufficiale apparirà qui da solo.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
